import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Lock } from "lucide-react";

import { DocumentRequestSubmitModal } from "@/components/DocumentRequestSubmitModal";
import { Button } from "@/components/ui/Button";
import { apiFetch, ApiError } from "@/lib/api";
import {
  controlledDocumentIsDownloadable,
  controlledDocumentIsPreviewable,
  controlledDocumentListActiveRevisionLabel,
  controlledDocumentListStatusSummary,
  downloadControlledDocument,
  filenameExtension,
  groupControlledDocumentsForList,
  type ControlledDocument,
} from "@/lib/documents";
import { ControlledDocumentDetailModal } from "@/pages/documents/ControlledDocumentDetailModal";
import { RelatedEngineeringChangesPanel } from "@/pages/documents/RelatedEngineeringChangesPanel";
import { DocumentShareModal } from "@/pages/documents/DocumentSharePanel";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

type SkuControlledDocument = ControlledDocument;

function statusClass(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "current") return "bg-green-50 text-green-700";
  if (status === "wip") return "bg-amber-50 text-amber-800";
  if (status === "draft") return "bg-blue-50 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

function statusChipLabel(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "wip") return "WIP";
  return status;
}

export function SkuDocumentsTab(props: { sku: string }): JSX.Element {
  const documentsAdmin = useHubModuleAdmin("documents");
  const [openDocument, setOpenDocument] = useState<SkuControlledDocument | null>(null);
  const [requestModal, setRequestModal] = useState<
    | { kind: "access"; document: SkuControlledDocument }
    | { kind: "new_document" }
    | null
  >(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const documentsQuery = useQuery<
    {
      documents: SkuControlledDocument[];
      access?: { documentsModule: boolean; documentsAccess?: string; sharedOnly: boolean };
    },
    ApiError
  >({
    queryKey: ["documents", "sku-detail", props.sku],
    queryFn: () =>
      apiFetch<{
        documents: SkuControlledDocument[];
        access?: { documentsModule: boolean; documentsAccess?: string; sharedOnly: boolean };
      }>("/api/document-accessible-for-sku", { query: { sku: props.sku } }),
    enabled: props.sku.length > 0,
    refetchOnWindowFocus: false,
  });

  const sortedDocumentGroups = useMemo(() => {
    const groups = groupControlledDocumentsForList(documentsQuery.data?.documents ?? []);
    return [...groups].sort((groupA, groupB) => {
      const documentA = groupA.representative;
      const documentB = groupB.representative;
      const kindCompare = documentA.document_kind_label.localeCompare(documentB.document_kind_label, undefined, {
        sensitivity: "base",
      });
      if (kindCompare !== 0) return kindCompare;
      return documentA.title.localeCompare(documentB.title, undefined, { sensitivity: "base" });
    });
  }, [documentsQuery.data?.documents]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        {documentsAdmin ? (
          <Button type="button" variant="secondary" size="sm" onClick={() => setShareModalOpen(true)}>
            Share documents
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setRequestModal({ kind: "new_document" })}
        >
          Request a document
        </Button>
        <Link
          to="/personal/document-requests"
          className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          My document requests
        </Link>
      </div>

      {documentsQuery.data != null && documentsQuery.data.access?.documentsAccess !== "admin" ? (
        <p className="text-sm text-slate-600">
          Catalog titles are always visible on this part number. Preview needs Document Control read-write
          or administrator access, or a share. Download needs administrator access or a share. Locked files
          can be requested.
        </p>
      ) : null}

      {documentsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading documents…</p>
      ) : documentsQuery.error ? (
        <p className="text-sm text-rose-700">{documentsQuery.error.message}</p>
      ) : sortedDocumentGroups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <p className="text-sm text-slate-600">No controlled documents are targeted directly at this part number.</p>
          <Button
            type="button"
            className="mt-3"
            variant="secondary"
            size="sm"
            onClick={() => setRequestModal({ kind: "new_document" })}
          >
            Request a document
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Document type</th>
                <th className="px-3 py-2">File type</th>
                <th className="px-3 py-2">Active rev</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {sortedDocumentGroups.map((group) => {
                const document = group.representative as SkuControlledDocument;
                const canPreview = controlledDocumentIsPreviewable(document);
                const canDownload = controlledDocumentIsDownloadable(document);
                const listStatus = controlledDocumentListStatusSummary(group.revisions);
                return (
                  <tr
                    key={group.key}
                    className={canPreview ? "cursor-pointer hover:bg-slate-50" : "bg-slate-50/50"}
                    onClick={() => {
                      if (canPreview) {
                        setOpenDocument(document);
                      }
                    }}
                  >
                    <td className="max-w-lg px-3 py-3 font-medium text-slate-900">
                      <span className="inline-flex items-center gap-2">
                        {!canPreview ? <Lock className="h-4 w-4 shrink-0 text-slate-400" aria-hidden /> : null}
                        {document.title}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">{document.document_kind_label}</td>
                    <td className="whitespace-nowrap px-3 py-3 tabular-nums text-slate-700">
                      {filenameExtension(document.original_filename) ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-600">
                      {controlledDocumentListActiveRevisionLabel(group.revisions)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass(listStatus)}`}>
                        {statusChipLabel(listStatus)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      {canDownload ? (
                        <HoverHintTrigger
                          trigger={
                            <button
                              type="button"
                              className="inline-flex rounded-md p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                              aria-label={`Download ${document.title}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                void downloadControlledDocument(document);
                              }}
                            >
                              <Download className="h-4 w-4" aria-hidden />
                            </button>
                          }
                        >
                          Download without opening the document viewer.
                        </HoverHintTrigger>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRequestModal({ kind: "access", document });
                          }}
                        >
                          Request access
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openDocument ? (
        <ControlledDocumentDetailModal
          document={openDocument}
          skuContext={props.sku}
          layout="viewer"
          onClose={() => setOpenDocument(null)}
        />
      ) : null}

      {shareModalOpen ? (
        <DocumentShareModal open sku={props.sku} onClose={() => setShareModalOpen(false)} />
      ) : null}

      <RelatedEngineeringChangesPanel sku={props.sku} detailPathPrefix="/documents/change-orders" />

      {requestModal?.kind === "access" ? (
        <DocumentRequestSubmitModal
          open
          kind="access"
          sku={props.sku}
          document={requestModal.document}
          onClose={() => setRequestModal(null)}
        />
      ) : requestModal?.kind === "new_document" ? (
        <DocumentRequestSubmitModal
          open
          kind="new_document"
          sku={props.sku}
          onClose={() => setRequestModal(null)}
        />
      ) : null}
    </div>
  );
}
